import { useCallback, useEffect, useState } from 'react';
import axios from 'src/utils/axios';
import { DEFAULT_ENTER_KEY, ENTER_KEY_NEWLINE, ENTER_KEY_SEND } from './enterKeyPreference';

const ENDPOINT = '/api/page-preferences/connections-composer';
const isValid = (value) => value === ENTER_KEY_NEWLINE || value === ENTER_KEY_SEND;

// Kept for the page session so reopening a chat does not flash the default.
// Held with the user it belongs to: Sign In As, or signing out and in as
// someone else without a reload, must not carry one consultant's choice to
// the next.
let cache = { userKey: null, enterKey: null };

const cachedFor = (userKey) => (cache.userKey === userKey ? cache.enterKey : null);

const remember = (userKey, enterKey) => {
  cache = { userKey, enterKey };
};

// Loads the saved mode once per user, then saves each change at once. The
// change shows immediately; if the save fails it is put back and onSaveError
// is called.
function useEnterKeyPreference({ userKey = null, onSaveError = () => { } } = {}) {
  const [enterKey, setEnterKey] = useState(cachedFor(userKey) || DEFAULT_ENTER_KEY);

  useEffect(() => {
    if (!userKey) {
      return undefined;
    }
    const cached = cachedFor(userKey);
    setEnterKey(cached || DEFAULT_ENTER_KEY);
    if (cached) {
      return undefined;
    }
    let cancelled = false;
    axios.get(ENDPOINT)
      .then((res) => {
        const value = res && res.data && res.data.enterKey;
        if (isValid(value) && !cancelled) {
          remember(userKey, value);
          setEnterKey(value);
        }
      })
      // The default is today's behavior, so a failed load needs no message.
      .catch(() => { });
    return () => { cancelled = true; };
  }, [userKey]);

  const changeEnterKey = useCallback(async (next) => {
    if (!isValid(next) || !userKey) {
      return;
    }
    const previous = cachedFor(userKey) || DEFAULT_ENTER_KEY;
    remember(userKey, next);
    setEnterKey(next);
    try {
      await axios.put(ENDPOINT, { enterKey: next });
    } catch (err) {
      remember(userKey, previous);
      setEnterKey(previous);
      onSaveError(err);
    }
  }, [userKey, onSaveError]);

  return { enterKey, changeEnterKey };
}

export default useEnterKeyPreference;
